/**
 * Shared markdown rendering for help articles — R-131-SUP-C
 */
// R-131-SUP-C
import React from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const helpMarkdownComponents = {
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const isInternal = href?.startsWith('/');
    if (isInternal && href) {
      return (
        <Link to={href} className="text-info underline underline-offset-2 hover:text-info/80" {...props}>
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className="text-info underline underline-offset-2 hover:text-info/80"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  },
  table: ({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm" {...props}>
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
    <th className="border border-border bg-surface-secondary px-3 py-2 text-left font-semibold" {...props}>
      {children}
    </th>
  ),
  td: ({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
    <td className="border border-border px-3 py-2 align-top" {...props}>
      {children}
    </td>
  ),
};

interface HelpMarkdownProps {
  children: string;
  className?: string;
}

export function HelpMarkdown({ children, className }: HelpMarkdownProps): React.ReactElement {
  return (
    <article className={className ?? 'prose prose-neutral dark:prose-invert max-w-none prose-headings:scroll-mt-8 prose-a:no-underline'}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={helpMarkdownComponents}>
        {children}
      </ReactMarkdown>
    </article>
  );
}
